<template>
  <!-- v3 contextual drawer (ui_layout === 'V2'). Sits beside the app rail and
       shows, for the active top-level area: the router sub-nav (its ToC items)
       plus any page-registered context (filters / hierarchy nav) via useV3Drawer.
       Hidden when there's nothing to show or the rail toggle collapses it.
       Mirrors the rm-portal-beta ContextDrawer. -->
  <aside v-if="visible" class="v3-drawer" aria-label="On this page" :style="{ width: drawerWidth + 'px' }">
    <div class="v3-drawer__scroll">
      <div v-if="heading" class="v3-drawer__head">
        <h2>{{ heading }}</h2>
      </div>

      <template v-if="items.length">
        <button
          class="v3-drawer__sec v3-drawer__sec--btn"
          type="button"
          :aria-expanded="!secCollapsed"
          @click="secCollapsed = !secCollapsed"
        >
          <span class="v3-drawer__ms">dashboard</span>
          On this page
          <span class="v3-drawer__ms v3-drawer__chev" :class="{ 'is-open': !secCollapsed }">expand_more</span>
        </button>

        <nav v-show="!secCollapsed" class="v3-drawer__toc">
          <template v-for="(g, gi) in groupedItems">
            <div v-if="g.section" :key="'sec' + gi" class="v3-drawer__subsec">{{ g.section }}</div>
            <button
              v-for="e in g.items"
              :key="e.key"
              type="button"
              class="v3-drawer__item"
              :class="{ 'is-active': e.kind === 'route' && e.routeName === currentRouteName }"
              :title="e.text"
              :aria-current="e.kind === 'route' && e.routeName === currentRouteName ? 'page' : undefined"
              @click="onItemClick(e)"
            >
              <span class="v3-drawer__ms">{{ e.icon }}</span>
              <span class="v3-drawer__lbl">{{ e.text }}</span>
            </button>
          </template>
        </nav>
      </template>

      <!-- Page-contributed context (e.g. All Jobs filters). -->
      <component :is="contextComponent" v-if="contextComponent" v-bind="contextProps" />
    </div>

    <!-- Drag-resize handle (design v3-drawer-resize): hover line + floating
         grip pill that follows the cursor; drag to resize, double-click resets. -->
    <div
      class="v3-drawer__resize"
      title="Resize — drag · Reset — double-click"
      role="separator"
      aria-orientation="vertical"
      @mousedown="startResize"
      @dblclick="resetWidth"
      @mousemove="onResizeHover"
      @mouseleave="grip = null"
    >
      <span class="line" :style="lineStyle"></span>
      <span v-if="grip" class="v3-drawer__dgrip" :style="{ top: grip.y + 'px' }" aria-hidden="true">
        <span></span><span></span><span></span>
      </span>
    </div>
  </aside>
</template>

<script>
import { computed, ref, getCurrentInstance } from 'vue';
import { useV3Nav } from '@/composables/useV3Nav';
import { useV3Chrome } from '@/composables/useV3Chrome';
import { useV3Drawer } from '@/composables/useV3Drawer';
import { useHomeToc } from '@/composables/useHomeToc';

export default {
  name: 'ContextDrawer',

  setup() {
    const instance = getCurrentInstance().proxy;
    const secCollapsed = ref(false);

    const { railItems, tocFor, activeArea } = useV3Nav({
      router: instance.$router,
      loginState: instance.$loginState,
      getCurrentRouteName: () => instance.$route && instance.$route.name,
    });
    const { sidebarCollapsed } = useV3Chrome();
    const { contextComponent, contextProps } = useV3Drawer();
    const { entries: homeEntries } = useHomeToc();

    const currentRouteName = computed(() => instance.$route && instance.$route.name);

    // Items: on the Home area, its router sub-nav (Tasks / Notifications) plus
    // the page's widget anchors ("On this page"); elsewhere, the area's router
    // sub-nav (items may carry a `section` label).
    const items = computed(() => {
      const routeItems = tocFor(activeArea.value).map((e) => ({ ...e, kind: 'route' }));
      if (activeArea.value === 'home') {
        const anchors = (homeEntries.value || []).map((e) => ({
          key: e.id,
          text: e.label,
          icon: e.icon,
          kind: 'anchor',
          anchorId: e.id,
        }));
        return [...routeItems, ...anchors];
      }
      return routeItems;
    });

    // Group by `section` label, preserving order (ungrouped items come first
    // when they precede any labelled section).
    const groupedItems = computed(() => {
      const out = [];
      const idx = {};
      (items.value || []).forEach((it) => {
        const key = it.section || '';
        if (!(key in idx)) {
          idx[key] = out.length;
          out.push({ section: it.section || null, items: [] });
        }
        out[idx[key]].items.push(it);
      });
      return out;
    });

    // Heading = the active area's rail label (group name or top-level page).
    const heading = computed(() => {
      const item = railItems.value.find((i) => i.key === activeArea.value);
      return item ? item.text : '';
    });

    // Only take up space when there's something to show.
    const visible = computed(
      () => !sidebarCollapsed.value && (items.value.length > 0 || !!contextComponent.value),
    );

    let flashTimer = null;
    function scrollToAnchor(id) {
      const el = document.getElementById(id);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.add('v3-widget-flash');
      if (flashTimer) clearTimeout(flashTimer);
      flashTimer = setTimeout(() => el.classList.remove('v3-widget-flash'), 1200);
    }

    function onItemClick(e) {
      if (!e) return;
      if (e.kind === 'anchor') {
        scrollToAnchor(e.anchorId);
        return;
      }
      if (!e.routeName || e.routeName === instance.$route.name) return;
      instance.$router
        .push({ name: e.routeName, params: { ...instance.$route.params } })
        .catch(() => {});
    }

    // --- Drag-resize (design v3-drawer-resize): clamp 220–360, persist per
    // browser, double-click resets to the default width. ---
    const DRAWER_W_KEY = 'v3-drawer-width';
    const DRAWER_W_DEFAULT = 236;
    const clampW = (w) => Math.min(360, Math.max(220, Math.round(w)));
    const loadWidth = () => {
      const saved = Number(localStorage.getItem(DRAWER_W_KEY));
      return saved && !isNaN(saved) ? clampW(saved) : DRAWER_W_DEFAULT;
    };
    const drawerWidth = ref(loadWidth());
    const grip = ref(null);

    function persistWidth() {
      try { localStorage.setItem(DRAWER_W_KEY, String(drawerWidth.value)); } catch { /* no storage */ }
    }
    function startResize(e) {
      e.preventDefault();
      const startX = e.clientX;
      const startW = drawerWidth.value;
      const move = (ev) => { drawerWidth.value = clampW(startW + ev.clientX - startX); };
      const up = () => {
        window.removeEventListener('mousemove', move);
        window.removeEventListener('mouseup', up);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        persistWidth();
      };
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', up);
    }
    function resetWidth() {
      drawerWidth.value = DRAWER_W_DEFAULT;
      persistWidth();
    }
    // Grip pill follows the cursor along the handle; the hover line is a 140px
    // segment centred on it (the design's exact behaviour).
    function onResizeHover(e) {
      const r = e.currentTarget.getBoundingClientRect();
      grip.value = { y: Math.min(r.height - 30, Math.max(30, e.clientY - r.top)), h: r.height };
    }
    const lineStyle = computed(() => {
      if (!grip.value) return { opacity: 0 };
      const top = Math.max(16, grip.value.y - 70);
      const height = Math.min(grip.value.h - 16, grip.value.y + 70) - top;
      return { top: top + 'px', height: height + 'px', opacity: 1 };
    });

    return { visible, heading, items, groupedItems, currentRouteName, contextComponent, contextProps, onItemClick, secCollapsed, drawerWidth, grip, lineStyle, startResize, resetWidth, onResizeHover };
  },
};
</script>

<style lang="scss" scoped>
.v3-drawer {
  flex: 0 0 auto;
  // Width is dynamic (drag-resize, persisted); the 236px default lives in setup.
  height: 100%;
  min-height: 0;
  background: var(--cc-surface, #ffffff);
  border-right: 1px solid var(--cc-border, #e6e8ec);
  display: flex;
  flex-direction: column;
  position: relative;
  // Visible so the resize handle can protrude past the right edge; the inner
  // scroll region still clips its own content.
  overflow: visible;
  // Curved join off the dark rail (matches the content plane / beta workspace).
  border-top-left-radius: 16px;

  &__scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 12px 10px;

    &::-webkit-scrollbar { width: 6px; }
    &::-webkit-scrollbar-thumb { background: var(--cc-border, #d3d7de); border-radius: 3px; }
    &::-webkit-scrollbar-track { background: transparent; }
  }

  &__head {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 6px 10px;

    h2 {
      margin: 0;
      font-size: 14.5px;
      font-weight: 700;
      letter-spacing: -0.01em;
      color: var(--cc-text, #1c2230);
      flex: 1;
    }
  }

  // "On this page" — collapsible section header (matches beta .v3-drawer-sec).
  &__sec {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    padding: 14px 8px 4px;
    border: 0;
    background: transparent;
    cursor: pointer;
    font-family: inherit;
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: var(--cc-text-3, #8b94a3);

    &:hover { color: var(--cc-text-2, #47505f); }
    .v3-drawer__ms { font-size: 14px; }
  }

  &__chev {
    margin-left: auto;
    font-size: 16px !important;
    transition: transform 0.12s ease;
    &.is-open { transform: rotate(180deg); }
  }

  // Labelled sub-group header within the ToC (from meta.v3.section).
  &__subsec {
    padding: 10px 8px 3px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--cc-text-3, #a2a9b6);
  }

  &__toc {
    display: flex;
    flex-direction: column;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 9px;
    width: 100%;
    min-height: 32px;
    border: 0;
    background: transparent;
    color: var(--cc-text-2, #47505f);
    padding: 5px 8px;
    border-radius: 8px;
    cursor: pointer;
    font-family: inherit;
    font-size: 13px;
    font-weight: 500;
    text-align: left;
    transition: background 120ms, color 120ms;

    &:hover { background: var(--cc-surface-2, #f2f4f7); color: var(--cc-text, #1c2230); }
    &:focus-visible { outline: none; box-shadow: 0 0 0 2px #a78bfa; }

    &.is-active {
      background: var(--cc-nav-accent-soft, rgba(79, 70, 229, 0.08));
      color: var(--cc-text, #1c2230);
      font-weight: 650;
      .v3-drawer__ms { color: var(--cc-nav-accent, #4f46e5); }
    }
  }

  &__ms { font-size: 18px; color: var(--cc-text-3, #8b94a3); flex: 0 0 auto; }

  &__lbl {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // ---- drag-resize handle (design v3-drawer-resize / v3-dgrip) ----
  &__resize {
    position: absolute;
    top: 0;
    right: -4px;
    bottom: 0;
    width: 9px;
    cursor: col-resize;
    z-index: 5;

    .line {
      position: absolute;
      right: 3px;
      width: 2px;
      border-radius: 2px;
      opacity: 0;
      background: linear-gradient(180deg, transparent, var(--cc-text-3, #8b93a6) 35%, var(--cc-text-3, #8b93a6) 65%, transparent);
      transition: opacity 120ms;
    }

    &:hover .line { opacity: 1; }
  }

  &__dgrip {
    position: absolute;
    right: -4px;
    transform: translateY(-50%);
    width: 15px;
    height: 30px;
    border-radius: 999px;
    border: 1px solid var(--cc-border, #e4e8f2);
    background: var(--cc-surface, #ffffff);
    box-shadow: 0 4px 10px rgba(16, 24, 40, 0.1);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2.5px;
    pointer-events: none;

    span {
      width: 7px;
      height: 1.5px;
      border-radius: 2px;
      background: var(--cc-text-3, #8b93a6);
    }
  }
}
</style>

<!-- Glyph font declared globally (unscoped) so it applies to the drawer's
     Material Symbols spans regardless of scoped-attribute matching. -->
<style lang="scss">
.v3-drawer__ms {
  font-family: 'Material Symbols Rounded' !important;
  font-weight: normal;
  font-style: normal;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  -webkit-font-smoothing: antialiased;
  font-feature-settings: 'liga';
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
  user-select: none;
  display: inline-flex;
}

/* Dark theme (uitheme-dark / uitheme-highcontrast on <v-app>): the drawer's
   scoped rules consume --cc-* vars with light fallbacks, so dark mode is just
   assigning the palette here (vars inherit into scoped rules — no specificity
   battle). Values mirror the v3 dark token set. */
.uitheme-dark .v3-drawer,
.uitheme-highcontrast .v3-drawer {
  --cc-surface: #181d25;
  --cc-surface-2: #20262f;
  --cc-border: #2c333e;
  --cc-text: #e7eaf0;
  --cc-text-2: #aeb6c2;
  --cc-text-3: #727b89;
  --cc-nav-accent: #9e8df6;
  --cc-nav-accent-soft: rgba(124, 108, 255, 0.16);
}
</style>
